import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useIntl } from "react-intl";
import { ROOT_API } from "../../../api/config";
import "./style.scss";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

const euro = (minor, currency) =>
  minor != null ? `${(minor / 100).toFixed(2)} ${currency || "EUR"}` : "—";

const PLANS = ["free", "premium", "business"];
const PLAN_LABEL = { free: "Free", premium: "Premium", business: "Business" };

export default function Subscriptions() {
  const intl = useIntl();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${ROOT_API}apis/admin/users`, {
        headers: authHeaders(),
      });
      setUsers(res?.data?.data || []);
    } catch (e) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setPlan = async (user, plan) => {
    if (user.plan === plan) return;
    const confirmed = await Swal.fire({
      icon: "question",
      title: intl.formatMessage({ id: "admin_confirm_set_plan" }, { plan: PLAN_LABEL[plan] }),
      text: user.email,
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({ id: "admin_apply" }),
      cancelButtonText: intl.formatMessage({ id: "register_service_delete_cancel" }),
    });
    if (!confirmed.isConfirmed) return;
    try {
      setBusy(user.email);
      await axios.post(
        `${ROOT_API}apis/admin/set-plan`,
        { email: user.email, plan },
        { headers: authHeaders() }
      );
      await load();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: intl.formatMessage({ id: "there_are_error_occured" }),
      });
    } finally {
      setBusy(null);
    }
  };

  const markInvoice = async (paymentId) => {
    try {
      setBusy(`inv${paymentId}`);
      await axios.post(
        `${ROOT_API}apis/admin/mark-invoice`,
        { payment_id: paymentId },
        { headers: authHeaders() }
      );
      await load();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: intl.formatMessage({ id: "there_are_error_occured" }),
      });
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-subs__state">
        {intl.formatMessage({ id: "dashboard_loading" })}
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const rows = q
    ? users.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.full_name || "").toLowerCase().includes(q)
      )
    : users;

  return (
    <div className="admin-subs">
      <input
        className="admin-subs__search"
        placeholder={intl.formatMessage({ id: "admin_search" })}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <table className="admin-subs__table">
        <thead>
          <tr>
            <th>{intl.formatMessage({ id: "admin_col_user" })}</th>
            <th>{intl.formatMessage({ id: "admin_col_tier" })}</th>
            <th>{intl.formatMessage({ id: "admin_col_addresses" })}</th>
            <th>{intl.formatMessage({ id: "admin_col_invoice" })}</th>
            <th>{intl.formatMessage({ id: "admin_col_actions" })}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.email}>
              <td>
                <div className="admin-subs__name">
                  {u.full_name || "—"}
                  {u.role === "admin" ? " 👑" : ""}
                  {(u.account_status || u.status) === "unverified" ? (
                    <span className="admin-subs__unverified">unverified</span>
                  ) : null}
                </div>
                <div className="admin-subs__email">{u.email}</div>
              </td>
              <td>
                <span className={`admin-subs__badge admin-subs__badge--${u.plan}`}>
                  {PLAN_LABEL[u.plan] || u.plan}
                </span>
                {u.premium_until ? (
                  <div className="admin-subs__until">
                    {intl.formatMessage({ id: "admin_premium_until" })} {u.premium_until}
                  </div>
                ) : null}
              </td>
              <td className="admin-subs__center">{u.addresses}</td>
              <td>
                {u.last_payment && u.last_payment.state === "completed" ? (
                  <div>
                    <div>{euro(u.last_payment.amount_minor, u.last_payment.currency)}</div>
                    <span
                      className={`admin-subs__inv admin-subs__inv--${u.last_payment.invoice_status}`}
                    >
                      {intl.formatMessage({
                        id:
                          u.last_payment.invoice_status === "issued"
                            ? "admin_invoice_issued"
                            : "admin_invoice_pending",
                      })}
                    </span>
                    {u.last_payment.invoice_status === "pending" &&
                    u.last_payment.state === "completed" ? (
                      <button
                        type="button"
                        className="admin-subs__btn admin-subs__btn--small"
                        disabled={busy === `inv${u.last_payment.id}`}
                        onClick={() => markInvoice(u.last_payment.id)}
                      >
                        {intl.formatMessage({ id: "admin_mark_invoice" })}
                      </button>
                    ) : null}
                  </div>
                ) : (
                  "—"
                )}
              </td>
              <td>
                <div className="admin-subs__actions">
                  {PLANS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`admin-subs__set${u.plan === p ? " is-current" : ""}`}
                      disabled={busy === u.email || u.plan === p}
                      onClick={() => setPlan(u, p)}
                    >
                      {PLAN_LABEL[p]}
                    </button>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
